import Reveal from "@/components/feature/Reveal";
import SectionTitle from "@/components/feature/SectionTitle";
import { pricePlans, priceNotes } from "@/mocks/price";
import { FORM_ANCHOR, TEL_DISPLAY, TEL_HREF, LINE_URL, LINE_COLOR, PAYMENT_IMAGE } from "@/constants";

export default function Price() {
  return (
    <section id="price" className="bg-background-50 py-16 md:py-24">
      <div className="site-container">
        <Reveal>
          <SectionTitle
            upper="PRICE"
            under="料金は荷物の種類・量・搬出条件・階段作業・必要人数などによって変わります。以下はあくまで目安です。"
          >
            料金の目安
          </SectionTitle>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {pricePlans.map((plan, i) => (
            <Reveal key={plan.name} delay={i * 60}>
              <div
                className={`relative flex h-full flex-col items-center rounded-lg border p-5 text-center ${
                  plan.featured ? "border-2 border-primary-400 bg-primary-50" : "border-background-200 bg-background-100/70"
                }`}
              >
                {plan.featured ? (
                  <span className="absolute -top-3 rounded-full bg-accent-500 px-3 py-1 text-[10px] font-black text-background-50">
                    よくご利用いただいています
                  </span>
                ) : null}
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-md ${
                    plan.featured ? "bg-primary-500 text-background-50" : "bg-primary-100 text-primary-700"
                  }`}
                >
                  <i className={`${plan.icon} text-2xl`} />
                </span>
                <p className="mt-3 font-heading text-base font-black text-foreground-950">{plan.name}</p>
                <p className="mt-0.5 text-sm font-medium text-foreground-600">{plan.size}</p>
                <p className="mt-3 font-heading text-3xl font-black leading-none text-primary-700">
                  {plan.price}
                  <span className="ml-0.5 text-sm font-bold">円〜</span>
                </p>
                <p className="mt-2 text-xs font-medium text-foreground-500">{plan.note}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={80}>
          <div className="mt-4 flex flex-col items-center justify-between gap-4 rounded-lg border-2 border-primary-300 bg-primary-50 p-5 sm:flex-row">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-home-heart-line text-2xl" />
              </span>
              <div>
                <p className="font-heading text-base font-black text-foreground-950">遺品整理・一軒家丸ごと</p>
                <p className="text-sm text-foreground-600">物量や作業内容によって大きく変わるため、現地調査のうえご提案します。</p>
              </div>
            </div>
            <p className="shrink-0 font-heading text-lg font-black text-primary-800">現地調査・個別見積もり</p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <ul className="mt-6 space-y-1.5">
            {priceNotes.map((note) => (
              <li key={note} className="flex items-start gap-2 text-sm leading-relaxed text-foreground-500">
                <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[5px] text-foreground-400" />
                {note}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={140}>
          <div className="mt-6 rounded-lg border border-background-200 bg-background-100/70 p-5 md:p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
                <i className="ri-wallet-3-line text-xl" />
              </span>
              <div>
                <p className="font-heading text-lg font-black text-foreground-950">お支払い方法</p>
                <p className="text-sm text-foreground-600">作業後のお支払いに対応しています。</p>
              </div>
            </div>
            <img
              src={PAYMENT_IMAGE.src}
              alt={PAYMENT_IMAGE.alt}
              className="mt-4 block h-auto w-full"
            />
            <p className="mt-4 flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
              <i className="ri-checkbox-circle-fill mt-0.5 text-primary-600" />
              現金のほか、各種クレジットカード・電子マネー・QRコード決済にも対応可能です。
            </p>
          </div>
        </Reveal>

        <Reveal delay={160}>
          <div className="mt-7 flex flex-col items-center gap-3 rounded-lg bg-secondary-50 p-5 sm:flex-row sm:justify-between">
            <p className="flex items-center gap-2 text-sm font-bold text-secondary-900">
              <i className="ri-hand-coin-line text-lg" />
              見積もりは無料。まずはお気軽にご相談ください。
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href={TEL_HREF}
                className="flex cursor-pointer items-center justify-center gap-2 rounded-md bg-primary-600 px-5 py-3 font-heading font-black text-background-50 transition-all hover:bg-primary-700"
              >
                <i className="ri-phone-fill text-lg" />
                <span className="whitespace-nowrap text-sm">{TEL_DISPLAY}</span>
              </a>
              <a
                href={LINE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ backgroundColor: LINE_COLOR }}
                className="flex cursor-pointer items-center justify-center gap-2 rounded-md px-5 py-3 font-heading font-black text-white transition-all hover:brightness-95"
              >
                <i className="ri-line-fill text-lg" />
                <span className="whitespace-nowrap text-sm">LINEで写真相談</span>
              </a>
              <a
                href={FORM_ANCHOR}
                className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-primary-300 bg-background-50 px-5 py-3 font-heading font-black text-primary-700 transition-all hover:bg-primary-50"
              >
                <i className="ri-mail-send-line text-lg" />
                <span className="whitespace-nowrap text-sm">フォームで相談</span>
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}